import { useParams, Link } from 'react-router-dom';
import { describeTerms, describeGifts } from '@affily/shared/commission';
import { formatCents } from '@affily/shared/money';
import { PLATFORM_LABELS } from '@affily/shared/constants';
import { programPhase, intakeRefusal, formatDay } from '@affily/shared/programDates';
import { useFetch } from '../hooks/useFetch.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { Seo } from '../components/Seo.jsx';
import { TermsGrid, PerformanceStats, GiftList } from '../components/Terms.jsx';
import { EventsTable } from '../components/EventsTable.jsx';
import { prizeMeasure, prizeValue, COMPETITION_STATUS_LABELS } from '../lib/competition.js';
import { InlineCalculator } from '../components/InlineCalculator.jsx';
import { Button, Badge, Spinner, Alert } from '../components/ui.jsx';

/**
 * The public program page.
 *
 * Nothing on this page is behind auth. A logged-out visitor and a crawler read
 * the same commission rate, cookie window, payout schedule and content
 * requirements that a signed-in creator reads. The only thing signing in
 * changes is the button at the bottom.
 */
/**
 * The price half of the Offer, matching the server-rendered one: a number only
 * for a flat payout. A percent has no amount and a gift-only program has no
 * money, so neither gets a price — `NaN` or `0` would mislabel the offer.
 */
function jsonLdPrice(program) {
  if (
    (program.commissionType === 'flat_per_conversion' || program.commissionType === 'flat_per_post') &&
    Number.isInteger(program.flatAmountCents)
  ) {
    return {
      priceSpecification: {
        '@type': 'PriceSpecification',
        price: (program.flatAmountCents / 100).toFixed(2),
        priceCurrency: (program.currency ?? 'usd').toUpperCase(),
      },
    };
  }
  if (program.commissionType === 'percent_of_sale' && Number.isFinite(program.rateBps)) {
    return {
      priceSpecification: {
        '@type': 'PriceSpecification',
        description: `${program.rateBps / 100}% of sale`,
      },
    };
  }
  return {};
}

/**
 * A private program, for someone it hasn't shared its terms with: who runs it,
 * what it is about, and the way to apply. The terms follow once the brand
 * accepts them, or come with an invitation.
 */
function PrivateProgram({ program, user, isCreator }) {
  const brand = program.brand;
  return (
    <>
      <Seo title={`${program.title} — ${brand?.companyName ?? 'Private program'}`} noindex />
      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {brand && <p className="text-sm font-medium text-ink-muted">{brand.companyName}</p>}
            <h1 className="mt-2 text-4xl font-semibold tracking-tight">{program.title}</h1>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Badge>Private program</Badge>
              {program.categories?.map((c) => (
                <Badge key={c}>{c}</Badge>
              ))}
            </div>
          </div>
          <ApplyBox program={program} user={user} isCreator={isCreator} />
        </header>

        <section className="mt-8 rounded-lg border border-rule bg-paper-raised p-4">
          <h2 className="text-sm font-semibold">Terms</h2>
          <p className="mt-1.5 text-sm text-ink-muted">
            {brand?.companyName ?? 'The brand'} shares this program’s terms with the creators it invites
            or accepts. Apply, and you’ll see them as soon as you’re accepted — before you post anything.
          </p>
        </section>

        {program.description && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold">About this program</h2>
            <p className="mt-2 whitespace-pre-line text-ink-muted">{program.description}</p>
          </section>
        )}
        {program.contentRequirements && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold">Content requirements</h2>
            <p className="mt-2 whitespace-pre-line text-ink-muted">{program.contentRequirements}</p>
          </section>
        )}
      </article>
    </>
  );
}

export default function ProgramDetail() {
  const { slug } = useParams();
  const { user, isCreator } = useAuth();
  const { data, error, loading } = useFetch(`/programs/${slug}`);

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">{error.message}</h1>
        <Button to="/programs" variant="outline" className="mt-6">
          Browse all programs
        </Button>
      </div>
    );
  }

  const program = data.program;
  const brand = program.brand;
  if (program.termsHidden) return <PrivateProgram program={program} user={user} isCreator={isCreator} />;
  const isGiftOnly = program.commissionType === 'gift_only';

  // Marked up so the terms are machine-readable too — the whole point of
  // publishing them is that they can be found and compared.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: program.title,
    description:
      isGiftOnly
        ? [program.offerSummary, program.description].filter(Boolean).join('. ')
        : (program.description ?? undefined),
    category: program.categories?.join(', ') || undefined,
    seller: brand
      ? { '@type': 'Organization', name: brand.companyName, url: brand.website ?? undefined }
      : undefined,
    ...jsonLdPrice(program),
  };

  return (
    <>
      <Seo
        title={`${program.title} — ${program.offerSummary ?? describeTerms(program)}`}
        description={
          isGiftOnly
            ? `${brand?.companyName ?? 'This brand'} offers creators gifts instead of a commission. ${describeGifts(program.gifts)}. Tracked with a ${program.cookieWindowDays}-day cookie window. Full terms public, no application required to see the offer.`
            : `${brand?.companyName ?? 'This brand'} pays ${program.offerSummary ?? describeTerms(program)} with a ${program.cookieWindowDays}-day cookie window, settled ${program.payoutSchedule}. Full terms public, no application required to see the rate.`
        }
        type="article"
        jsonLd={jsonLd}
      />

      <article className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <nav className="text-sm text-ink-muted">
          <Link to="/programs" className="hover:text-ink">
            Programs
          </Link>
          {/* No slug means the brand's profile is unpublished: name it, link nowhere. */}
          {brand && (
            <>
              <span className="mx-1.5 text-ink-faint">/</span>
              {brand.slug ? (
                <Link to={`/brands/${brand.slug}`} className="hover:text-ink">
                  {brand.companyName}
                </Link>
              ) : (
                brand.companyName
              )}
            </>
          )}
        </nav>

        <header className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {brand && (
              <div className="flex items-center gap-2">
                {brand.logoUrl && (
                  <img
                    src={brand.logoUrl}
                    alt=""
                    className="size-7 rounded-md border border-rule object-cover"
                  />
                )}
                {brand.slug ? (
                  <Link
                    to={`/brands/${brand.slug}`}
                    className="text-sm font-medium text-ink-muted hover:text-ink"
                  >
                    {brand.companyName}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-ink-muted">{brand.companyName}</span>
                )}
              </div>
            )}
            <h1 className="mt-2 text-4xl font-semibold tracking-tight">{program.title}</h1>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {program.status !== 'active' && (
                <Badge tone="caution">
                  {program.status === 'paused' ? 'Paused — not accepting applications' : program.status}
                </Badge>
              )}
              {program.status === 'active' && programPhase(program) === 'ended' && (
                <Badge tone="caution">Ended {formatDay(program.endsAt)}</Badge>
              )}
              {program.status === 'active' && program.intake?.mode === 'window' && (
                <Badge tone={intakeRefusal(program) ? 'neutral' : 'positive'}>
                  {intakeRefusal(program) ?? `Applications close ${formatDay(program.intake.closesAt)}`}
                </Badge>
              )}
              {program.categories?.map((c) => (
                <Badge key={c}>{c}</Badge>
              ))}
              {program.autoApprove && <Badge tone="positive">Auto-approves applications</Badge>}
            </div>
          </div>

          {data.viewerIsOwner ? (
            <div className="flex flex-col items-start gap-2 sm:items-end">
              <span className="text-xs text-ink-muted">Your program</span>
              {/* A full load, so the dashboard opens on this program's brand (see useBrand). */}
              <Button
                href={`/dashboard/brand/programs/${program.id}${brand?.id ? `?brand=${brand.id}` : ''}`}
                size="sm"
              >
                Edit program
              </Button>
            </div>
          ) : (
            <ApplyBox program={program} user={user} isCreator={isCreator} />
          )}
        </header>

        <section className="mt-8" aria-labelledby="terms-heading">
          <h2
            id="terms-heading"
            className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint"
          >
            {program.visibility === 'private'
              ? 'Terms · shared with invited and accepted creators'
              : 'Terms · public, no application needed'}
          </h2>
          <TermsGrid program={program} />

          {program.estimatedTotalPerCustomerCents != null && program.isRecurring && (
            <p className="mt-2 text-xs text-ink-faint">
              On the brand’s stated average order, one customer is worth about{' '}
              <span className="font-medium text-ink">
                {formatCents(program.estimatedTotalPerCustomerCents, { currency: program.currency })}
              </span>{' '}
              to you across the full {program.recurringMonths}-cycle window — the number to compare
              against a higher one-time rate elsewhere.
            </p>
          )}
        </section>

        {program.gifts?.length > 0 && (
          <section className="mt-8" aria-labelledby="gifts-heading">
            <h2
              id="gifts-heading"
              className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint"
            >
              {isGiftOnly ? 'What you get · instead of a commission' : 'Free gifts · on top of the commission'}
            </h2>
            <div className="rounded-lg border border-rule bg-paper-raised p-4">
              <GiftList gifts={program.gifts} currency={program.currency} detailed />
            </div>
            <p className="mt-2 text-xs text-ink-faint">
              Retail values are the brand’s own figures — Affily hasn’t checked them. Gifts are
              separate from commission and don’t change what a conversion pays.
            </p>
          </section>
        )}

        {program.competition && (
          <section className="mt-8" aria-labelledby="competition-heading">
            <h2
              id="competition-heading"
              className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint"
            >
              Competition · {formatCents(program.competition.totalPrizeCents, { currency: program.currency?.toUpperCase(), dropZeroCents: true })}{' '}
              in prizes on top of the commission
            </h2>
            <div className="rounded-lg border border-rule bg-paper-raised">
              <ul className="divide-y divide-rule">
                {program.competition.prizes.map((prize) => (
                  <li key={prize.key} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
                    <span>
                      <span className="font-medium">{prize.name}</span>
                      <span className="block text-xs text-ink-muted">{prizeMeasure(prize, program.events)}</span>
                    </span>
                    <span className="text-right">
                      <span className="block font-medium tabular-nums">
                        {formatCents(prize.amountCents, { currency: program.currency?.toUpperCase(), dropZeroCents: true })}
                      </span>
                      {prize.award && (
                        <span className="block text-xs text-positive">
                          Won by {prize.award.creatorName}
                          {prize.award.metricValue != null && ` · ${prizeValue(prize, prize.award.metricValue, program.events)}`}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="border-t border-rule px-4 py-2 text-xs text-ink-muted">
                {COMPETITION_STATUS_LABELS[program.competition.status]}
                {program.competition.startsAt && ` · from ${new Date(program.competition.startsAt).toLocaleDateString()}`}{' '}
                · ends {new Date(program.competition.endsAt).toLocaleString()}
              </p>
            </div>
            <p className="mt-2 text-xs text-ink-faint">
              Counted actions are what Affily tracked; video numbers are read from TikTok, Instagram or
              YouTube through each creator’s own connected account. A “first to” prize goes to the
              first video Affily reads at or past the number — videos close to it are read every 15
              minutes, and two past it in the same read go to the one with more. The brand confirms
              each winner and pays the prize through Affily, like a commission.
            </p>
          </section>
        )}

        <EventsTable program={program} className="mt-8" />

        {/* A gift-only program has no commission to project. */}
        {!isGiftOnly && <InlineCalculator program={program} className="mt-8" />}

        {program.description && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold">About this program</h2>
            <p className="mt-2 max-w-2xl whitespace-pre-line text-ink-muted">
              {program.description}
            </p>
          </section>
        )}

        {program.contentRequirements && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold">Content requirements</h2>
            <p className="mt-2 max-w-2xl whitespace-pre-line text-ink-muted">
              {program.contentRequirements}
            </p>
          </section>
        )}

        {program.platformsWanted?.length > 0 && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold">Platforms wanted</h2>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {program.platformsWanted.map((p) => (
                <Badge key={p} tone="accent">
                  {PLATFORM_LABELS[p] ?? p}
                </Badge>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10" aria-labelledby="performance-heading">
          <div className="mb-2 flex items-baseline justify-between gap-4">
            <h2
              id="performance-heading"
              className="text-2xs font-semibold uppercase tracking-wider text-ink-faint"
            >
              Tracked performance · all-time
            </h2>
            <p className="text-xs text-ink-faint">
              Measured by Affily, not reported by the brand.
            </p>
          </div>
          <PerformanceStats performance={program.performance} currency={program.currency} />
          <p className="mt-2 text-xs text-ink-faint">
            These are the real numbers, good or bad. A dash means there isn’t enough traffic yet to
            compute the figure — we don’t show a zero we can’t stand behind.
          </p>
        </section>
      </article>
    </>
  );
}

function ApplyBox({ program, user, isCreator }) {
  if (program.status !== 'active') {
    return (
      <Alert tone="caution" className="max-w-xs">
        This program isn’t accepting applications right now. The terms stay public either way.
      </Alert>
    );
  }
  const refusal = intakeRefusal(program);
  if (refusal) {
    return (
      <Alert tone="caution" className="max-w-xs">
        {refusal}. The terms stay public either way.
      </Alert>
    );
  }

  if (!user) {
    return (
      <div className="max-w-xs text-right">
        <Button to={`/programs/${program.slug}/apply`} size="lg">
          Apply as a creator
        </Button>
        <p className="mt-1.5 text-xs text-ink-faint">
          Free to join — you make the account on the next page. You already have the full terms
          above.
        </p>
      </div>
    );
  }

  if (!isCreator) {
    return (
      <div className="max-w-xs text-right">
        <Button to={`/programs/${program.slug}/apply`} variant="outline" size="lg">
          Add a creator profile and apply
        </Button>
      </div>
    );
  }

  return (
    <Button to={`/programs/${program.slug}/apply`} size="lg">
      Apply to this program
    </Button>
  );
}
